<template>
    <div>
        <p class="pl-1">
            <v-icon class="mr-2">merge_type</v-icon>Need to merge instruments from another customer?
            <v-btn
                rounded
                outlined
                text
                x-small
                class="ml-2"
                @click="showDialog"
                >Click here</v-btn
            >
        </p>
        <v-dialog v-model="dialog.show" persistent :overlay="false" max-width="800px">
            <v-card>
            <v-card-title class="title secondary mb-4">Merge Customer Instruments</v-card-title>
            <v-card-text>
                <v-alert
                    v-if="!mergeStarted"
                    type="info"
                    outlined
                    class="mb-4"
                >
                    This will copy all instruments from the source customer to <strong>{{targetCustomerName}}</strong>.
                    Only instruments that the target customer doesn't already own will be added.
                    The source customer will keep their instruments (they won't be removed).
                </v-alert>

                <div v-if="!mergeStarted">
                    <p class="mb-2">
                        <strong>Source Customer (merge FROM):</strong>
                    </p>
                    <v-text-field
                        v-model="dialog.sourceClientId"
                        dense
                        filled
                        label="Source Customer ID"
                        type="number"
                        hint="Enter the Customer ID to merge instruments FROM"
                        persistent-hint
                        :disabled="loading"
                        @input="loadSourceCustomer"
                    ></v-text-field>

                    <div v-if="sourceCustomer" class="mb-4">
                        <v-chip small color="primary" class="mt-2">
                            {{sourceCustomer.Name}} ({{sourceCustomer.InstrumentCount}} instruments)
                        </v-chip>
                    </div>

                    <p class="mb-2 mt-4">
                        <strong>Target Customer (merge TO):</strong>
                    </p>
                    <v-text-field
                        v-model="targetClientId"
                        dense
                        filled
                        label="Target Customer ID"
                        type="number"
                        hint="This is the customer who will receive the instruments"
                        persistent-hint
                        disabled
                    ></v-text-field>

                    <div v-if="targetCustomerName" class="mb-4">
                        <v-chip small color="success" class="mt-2">
                            {{targetCustomerName}}
                        </v-chip>
                    </div>

                    <v-checkbox
                        v-if="sourceCustomer"
                        v-model="dialog.createShopifyOrders"
                        label="Create Shopify Orders"
                        hint="Creates a $0 Shopify order for each instrument that has a Shopify variant ID"
                        persistent-hint
                        hide-details="auto"
                        class="mt-2"
                    ></v-checkbox>
                </div>

                <div v-if="mergeStarted">
                    <v-progress-linear
                        v-if="merging"
                        indeterminate
                        color="primary"
                        class="mb-4"
                    ></v-progress-linear>

                    <v-alert
                        v-if="mergeResult"
                        :type="mergeResult.success ? 'success' : 'error'"
                        outlined
                        class="mb-4"
                    >
                        {{ mergeResult.message }}
                    </v-alert>

                    <div v-if="mergeResult && mergeResult.success">
                        <p><strong>Summary:</strong></p>
                        <ul>
                            <li>Instruments added: {{mergeResult.added}}</li>
                            <li>Instruments skipped (already owned): {{mergeResult.skipped}}</li>
                        </ul>

                        <div v-if="mergeResult.instrumentsAdded && mergeResult.instrumentsAdded.length > 0" class="mt-4">
                            <p><strong>Added instruments:</strong></p>
                            <v-chip
                                v-for="inst in mergeResult.instrumentsAdded"
                                :key="inst.id"
                                x-small
                                class="mr-2 mb-2"
                            >
                                {{inst.name}} (ID: {{inst.id}})
                            </v-chip>
                        </div>
                    </div>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    v-if="!mergeStarted"
                    color="primary"
                    text
                    @click="dialog.show = false"
                >Cancel</v-btn>
                <v-btn
                    v-if="mergeStarted"
                    color="primary"
                    text
                    @click="closeDialog"
                    :disabled="merging"
                >Close</v-btn>
                <v-btn
                    v-if="!mergeStarted"
                    color="primary"
                    @click="confirmMerge"
                    :loading="loading || merging"
                    :disabled="!canMerge"
                >
                    Merge Instruments
                </v-btn>
            </v-card-actions>
            </v-card>
        </v-dialog>
        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
            {{ snackText }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snack = false"> Close </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>


<script>
    import fb from "@/main";

    export default {
      props: ['targetClientId', 'targetCustomerName'],
      components: {
      },
      data: () => {
        return {
          loading: false,
          merging: false,
          mergeStarted: false,
          sourceCustomer: null,
          dialog: {
            show: false,
            sourceClientId: null,
            createShopifyOrders: false
          },
          mergeResult: null,
          snack: false,
          snackColor: "",
          snackText: "",
        }
      },
      computed: {
        canMerge() {
            return this.dialog.sourceClientId &&
                   this.targetClientId &&
                   this.dialog.sourceClientId != this.targetClientId &&
                   this.sourceCustomer &&
                   !this.loading &&
                   !this.merging;
        }
      },
      async created() {
          this.store = this.$route.fullPath.split("/")[1];
      },
      methods: {
        async loadSourceCustomer() {
            if (!this.dialog.sourceClientId) {
                this.sourceCustomer = null;
                return;
            }

            this.loading = true;
            try {
                const getCustomersCall = fb.functions.httpsCallable("getCustomers");
                const { data: customers } = await getCustomersCall({
                    store: this.store,
                    id: this.dialog.sourceClientId,
                });

                if (customers && customers.length > 0) {
                    const getCustomerInstrumentsCall = fb.functions.httpsCallable("getCustomerInstruments");
                    const { data: instruments } = await getCustomerInstrumentsCall({
                        store: this.store,
                        id: this.dialog.sourceClientId,
                    });

                    this.sourceCustomer = {
                        Id: customers[0].Id,
                        Name: customers[0].CustomerName,
                        InstrumentCount: instruments ? instruments.length : 0
                    };
                } else {
                    this.sourceCustomer = null;
                    this.snack = true;
                    this.snackColor = "error";
                    this.snackText = "Source customer not found";
                }
            } catch (error) {
                console.error('Error loading source customer:', error);
                this.sourceCustomer = null;
                this.snack = true;
                this.snackColor = "error";
                this.snackText = "Error loading source customer";
            } finally {
                this.loading = false;
            }
        },
        async confirmMerge() {
            if (!this.canMerge) {
                return;
            }

            const sourceId = parseInt(this.dialog.sourceClientId);
            const targetId = parseInt(this.targetClientId);

            if (sourceId === targetId) {
                this.snack = true;
                this.snackColor = "error";
                this.snackText = "Source and target customers cannot be the same";
                return;
            }

            let confirmMessage = `Are you sure you want to merge instruments from "${this.sourceCustomer.Name}" (ID: ${sourceId}) to "${this.targetCustomerName}" (ID: ${targetId})?\n\n` +
                `This will add ${this.sourceCustomer.InstrumentCount} instrument(s) from the source customer to the target customer (minus any duplicates).`;

            if (this.dialog.createShopifyOrders) {
                confirmMessage += '\n\nShopify orders will be created for instruments with variant IDs.';
            }

            const confirmed = confirm(confirmMessage);

            if (!confirmed) {
                return;
            }

            await this.performMerge(sourceId, targetId);
        },
        async performMerge(sourceClientId, targetClientId) {
            this.merging = true;
            this.mergeStarted = true;
            this.mergeResult = null;

            try {
                const mergeCustomerInstrumentsCall = fb.functions.httpsCallable("mergeCustomerInstruments");
                const result = await mergeCustomerInstrumentsCall({
                    store: this.store,
                    sourceClientId: sourceClientId,
                    targetClientId: targetClientId,
                    createShopifyOrders: this.dialog.createShopifyOrders
                });

                this.mergeResult = result.data;

                if (result.data.success) {
                    this.$emit("instrumentsMerged");
                }
            } catch (error) {
                console.error('Error merging instruments:', error);
                this.mergeResult = {
                    success: false,
                    message: error.message || "An error occurred during the merge"
                };
            } finally {
                this.merging = false;
            }
        },
        showDialog() {
            this.dialog = {
                show: true,
                sourceClientId: null,
                createShopifyOrders: false
            };
            this.sourceCustomer = null;
            this.mergeStarted = false;
            this.mergeResult = null;
        },
        closeDialog() {
            this.dialog.show = false;
            this.dialog.sourceClientId = null;
            this.dialog.createShopifyOrders = false;
            this.sourceCustomer = null;
            this.mergeStarted = false;
            this.mergeResult = null;
        }
      }
    }
</script>
